import { useMemo, useState, type FormEvent } from "react";
import { Building2, ChevronRight, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  useProfessionalAuth,
  type ProfessionalTenantOption
} from "../auth/ProfessionalAuthContext";
import { getDefaultAuthorizedRoute } from "../auth/permissions";
import "../styles/auth.css";

type TenantSelectionState = {
  selectionToken: string;
  options: ProfessionalTenantOption[];
} | null;

function normalizeError(error: unknown) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "No se pudo iniciar sesión";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, selectTenant } = useProfessionalAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectingTenant, setSelectingTenant] = useState<string | null>(null);
  const [tenantSelection, setTenantSelection] = useState<TenantSelectionState>(null);

  const canSubmit = useMemo(
    () => email.trim().length > 0 && password.trim().length > 0 && !submitting,
    [email, password, submitting]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setTenantSelection(null);

    try {
      setSubmitting(true);
      const result = await login(email, password);

      if (result.status === "select_tenant") {
        setTenantSelection({
          selectionToken: result.selectionToken,
          options: result.options
        });
        return;
      }

      navigate(getDefaultAuthorizedRoute(result.user.permissions), { replace: true });
    } catch (requestError) {
      setError(normalizeError(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTenantSelection(option: ProfessionalTenantOption) {
    if (!tenantSelection) {
      return;
    }

    setError(null);
    setSelectingTenant(option.membershipKey);

    try {
      const user = await selectTenant(tenantSelection.selectionToken, option.membershipKey);
      navigate(getDefaultAuthorizedRoute(user.permissions), { replace: true });
    } catch (requestError) {
      setError(normalizeError(requestError));
    } finally {
      setSelectingTenant(null);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-background" aria-hidden="true" />

      <main className="auth-page">
        <section className="auth-hero">
          <div className="auth-badge">
            <ShieldCheck size={16} />
            Acceso profesional
          </div>
          <h1 className="auth-title">Inicia sesión con tu cuenta de entrenador</h1>
          <p className="auth-copy">
            Accede solo a los negocios en los que estés dado de alta y entra directamente en tu espacio de trabajo.
          </p>

          <div className="auth-feature-list">
            <div className="auth-feature-card">
              <Building2 size={18} />
              <div>
                <strong>Multi-tenant</strong>
                <span>Si tu correo existe en varios negocios podrás elegir uno al entrar.</span>
              </div>
            </div>
            <div className="auth-feature-card">
              <UserRound size={18} />
              <div>
                <strong>Acceso controlado</strong>
                <span>Solo verás las secciones que tu negocio te haya habilitado desde el panel.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="auth-panel">
          <div className="auth-card">
            <div className="auth-card-header">
              <h2>Entrar</h2>
              <p>Tu negocio gestiona el alta de profesionales desde el panel de administración.</p>
            </div>

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <label className="auth-field">
                <span>Correo electrónico</span>
                <div className="auth-input-wrap">
                  <Mail size={18} />
                  <input
                    className="auth-input"
                    autoComplete="email"
                    inputMode="email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
              </label>

              <label className="auth-field">
                <span>Contraseña</span>
                <div className="auth-input-wrap">
                  <LockKeyhole size={18} />
                  <input
                    className="auth-input"
                    autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Introduce tu contraseña"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    className="auth-ghost-button"
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              {error ? <div className="auth-error">{error}</div> : null}

              <button className="auth-submit-button" type="submit" disabled={!canSubmit}>
                {submitting ? "Comprobando acceso..." : "Continuar"}
              </button>
            </form>
          </div>
        </section>
      </main>

      {tenantSelection ? (
        <div className="tenant-modal-backdrop" role="presentation">
          <div
            className="tenant-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-selection-title"
          >
            <div className="tenant-modal-header">
              <div>
                <span className="tenant-modal-kicker">Selecciona negocio</span>
                <h3 id="tenant-selection-title">Tu cuenta existe en varios centros</h3>
              </div>
              <button
                type="button"
                className="tenant-close-button"
                onClick={() => setTenantSelection(null)}
                aria-label="Cerrar selector de negocio"
              >
                ×
              </button>
            </div>

            <p className="tenant-modal-copy">
              Elige el negocio al que quieres acceder en esta sesión. Más adelante podrás cambiarlo cerrando sesión.
            </p>

            <div className="tenant-option-list">
              {tenantSelection.options.map((option) => (
                <button
                  key={option.membershipKey}
                  type="button"
                  className="tenant-option"
                  onClick={() => void handleTenantSelection(option)}
                  disabled={Boolean(selectingTenant)}
                >
                  <div className="tenant-option-icon">
                    <Building2 size={18} />
                  </div>
                  <div className="tenant-option-content">
                    <strong>{option.tenantName}</strong>
                    <span>{option.coachName || email.trim()}</span>
                  </div>
                  <ChevronRight size={18} className="tenant-option-chevron" />
                  {selectingTenant === option.membershipKey ? (
                    <span className="tenant-option-loading">Abriendo...</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
